<script setup>
import { Head, useForm, usePage } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import { computed, ref } from 'vue';
import { EnvelopeIcon, PaperAirplaneIcon, ShieldCheckIcon, ClockIcon, MapPinIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import { trackEvent } from '@/lib/tracking';
import { useDesignSet } from '@/composables/useDesignSet';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    spotImages: Object,
});

const { isClassic } = useDesignSet();
const page = usePage();

const domainName = computed(() => props.domain?.request_host || props.domain?.name || 'leadportal.scienceworks.com');
const contactEmail = computed(() => props.domain?.settings?.contact_email || (domainName.value.includes('scienceworks.com') ? 'info@scienceworks.com' : `info@${domainName.value}`));
const serviceLabel = computed(() => props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik');
const isBrandDirect = computed(() => checkBrandDirect(props.domain));
const brandName = computed(() => props.domain?.settings?.brand_name || domainName.value);

const form = useForm({
    name: '',
    email: '',
    subject: '',
    message: '',
    service_type: props.service || 'pv',
    website: '', // honeypot
});

const submitted = computed(() => page.props.flash?.contact_status === 'success');
const submitting = ref(false);

const submit = () => {
    if (!form.name || !form.email || !form.message) return;

    trackEvent('contact_form_submit', {
        event_category: 'engagement',
        service_type: props.service,
        metadata: { subject: form.subject },
    });

    form.post('/kontakt', {
        preserveScroll: true,
        onStart: () => { submitting.value = true; },
        onFinish: () => { submitting.value = false; },
        onSuccess: () => { form.reset('name', 'email', 'subject', 'message'); },
    });
};
</script>

<template>
    <Head :title="`Kontakt - ${serviceLabel}-Beratung`">
        <meta name="description" :content="`Kontaktieren Sie uns für eine kostenlose Beratung zu ${serviceLabel}. Unser Team berät Sie gerne.`" />
        <meta property="og:title" :content="`Kontakt - ${serviceLabel}-Beratung`" />
        <meta property="og:type" content="website" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="contact" :heroMode="!isClassic" :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
        <div class="smi-k">
        <!-- Hero -->
        <section
            class="ds-hero"
            :class="{ 'ds-hero--with-image': !isClassic && spotImages?.contact_illustration }"
            :style="!isClassic && spotImages?.contact_illustration ? { backgroundImage: `url(${spotImages.contact_illustration})` } : {}"
        >
            <div v-if="!isClassic" class="ds-hero__overlay"></div>
            <div class="ds-hero__inner">
                <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span v-if="!isClassic" class="ds-hero__badge">Kontakt</span>
                    <h1 class="ds-hero__title">Fragen? <span class="ds-hero__accent">Schreiben Sie uns.</span></h1>
                    <p class="ds-hero__subtitle">Fragen zum Vergleichsportal oder Ihrer Anfrage? Schreiben Sie uns - wir antworten in der Regel innerhalb von 24 Stunden.</p>
                </div>
            </div>
        </section>

        <!-- Contact Form + Details -->
        <section class="ds-section ds-section--gradient">
            <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="ds-contact-grid">
                    <!-- Left: Details -->
                    <div class="ds-contact-details">
                        <!-- Trust badge strip -->
                        <div class="ds-trust-strip">
                            <span class="ds-trust-strip__item">
                                <ShieldCheckIcon class="ds-trust-strip__icon" aria-hidden="true" />
                                Vergleichsportal
                            </span>
                            <span class="ds-trust-strip__sep" aria-hidden="true">|</span>
                            <span class="ds-trust-strip__item">
                                <ClockIcon class="ds-trust-strip__icon" aria-hidden="true" />
                                Antwort in 24h
                            </span>
                            <span class="ds-trust-strip__sep" aria-hidden="true">|</span>
                            <span class="ds-trust-strip__item">
                                <MapPinIcon class="ds-trust-strip__icon" aria-hidden="true" />
                                Kostenlos
                            </span>
                        </div>

                        <div class="ds-detail-card">
                            <div class="ds-detail-card__icon-wrap">
                                <EnvelopeIcon class="ds-detail-card__icon" />
                            </div>
                            <div>
                                <h3 class="ds-detail-card__label">E-Mail</h3>
                                <a :href="`mailto:${contactEmail}`" class="ds-detail-card__link">{{ contactEmail }}</a>
                                <p class="ds-detail-card__hint">Wir antworten in der Regel innerhalb von 24 Stunden.</p>
                            </div>
                        </div>

                        <div class="ds-detail-card">
                            <div class="ds-detail-card__icon-wrap ds-detail-card__icon-wrap--clock">
                                <ClockIcon class="ds-detail-card__icon" aria-hidden="true" />
                            </div>
                            <div>
                                <h3 class="ds-detail-card__label">Erreichbarkeit</h3>
                                <p class="ds-detail-card__text">Mo - Fr: 9:00 - 17:00 Uhr</p>
                                <p class="ds-detail-card__hint" style="margin-top: 8px;">Anfragen über das Formular werden auch außerhalb der Geschäftszeiten bearbeitet.</p>
                            </div>
                        </div>

                        <div class="ds-detail-card ds-detail-card--accent">
                            <div class="ds-detail-card__icon-wrap ds-detail-card__icon-wrap--info">
                                <InformationCircleIcon class="ds-detail-card__icon" aria-hidden="true" />
                            </div>
                            <div>
                                <h3 class="ds-detail-card__label">Über uns</h3>
                                <p v-if="isBrandDirect" class="ds-detail-card__text">{{ brandName }} ist ein Vergleichsportal für {{ serviceLabel }}. Wir vermitteln Ihre Anfrage an regionale Fachbetriebe - kostenlos und unverbindlich für Sie.</p>
                                <p v-else class="ds-detail-card__text">Wir sind ein Vergleichsportal und verbinden Hauseigentümer mit Fachbetrieben für {{ serviceLabel }}. Die Anfrage ist für Sie kostenlos und unverbindlich. Wir finanzieren uns über eine Vermittlungsvergütung der Fachbetriebe.</p>
                            </div>
                        </div>

                        <div
                            class="ds-contact-image"
                            :style="spotImages?.contact_illustration ? { backgroundImage: `url(${spotImages.contact_illustration})` } : { background: `linear-gradient(135deg, var(--dv-glow-a, rgba(137,111,218,0.14)) 0%, var(--dv-glow-b, rgba(251,144,125,0.16)) 100%)` }"
                        >
                            <div class="ds-contact-image__overlay"></div>
                        </div>
                    </div>

                    <!-- Right: Form -->
                    <div class="ds-form-card">
                        <div v-if="submitted" class="ds-form-card__success">
                            <div class="ds-form-card__success-icon-wrap">
                                <CheckCircleIcon class="ds-form-card__success-icon" aria-hidden="true" />
                            </div>
                            <h2 class="ds-form-card__success-title">Vielen Dank, Ihre Nachricht ist angekommen.</h2>
                            <p class="ds-form-card__success-text">Wir melden uns in der Regel innerhalb von 24 Stunden bei Ihnen. Bei dringenden Anliegen erreichen Sie uns auch direkt unter <a :href="`mailto:${contactEmail}`" class="ds-form-card__success-link">{{ contactEmail }}</a>.</p>
                        </div>
                        <form v-else @submit.prevent="submit" class="ds-form" novalidate>
                            <h2 class="ds-form__title">Nachricht senden</h2>
                            <p class="ds-form__intro">Alle mit * markierten Felder sind Pflichtfelder. Wir melden uns in der Regel innerhalb von 24 Stunden.</p>
                            <div class="ds-form__row">
                                <div class="ds-form__field">
                                    <label for="contact-name" class="ds-form__label">Ihr Name *</label>
                                    <input
                                        id="contact-name"
                                        v-model="form.name"
                                        type="text"
                                        placeholder="Max Mustermann"
                                        class="ds-form__input"
                                        :class="{ 'ds-form__input--error': form.name === '' && form.isDirty }"
                                        required
                                        autocomplete="name"
                                    />
                                    <span v-if="form.errors?.name" class="ds-form__error" role="alert">{{ form.errors.name }}</span>
                                </div>
                                <div class="ds-form__field">
                                    <label for="contact-email" class="ds-form__label">Ihre E-Mail *</label>
                                    <input
                                        id="contact-email"
                                        v-model="form.email"
                                        type="email"
                                        placeholder="max@beispiel.de"
                                        class="ds-form__input"
                                        :class="{ 'ds-form__input--error': form.errors?.email }"
                                        required
                                        autocomplete="email"
                                    />
                                    <span v-if="form.errors?.email" class="ds-form__error" role="alert">{{ form.errors.email }}</span>
                                </div>
                            </div>
                            <div class="ds-form__field">
                                <label for="contact-subject" class="ds-form__label">Betreff</label>
                                <input
                                    id="contact-subject"
                                    v-model="form.subject"
                                    type="text"
                                    placeholder="Worum geht es?"
                                    class="ds-form__input"
                                />
                            </div>
                            <div class="ds-form__field">
                                <label for="contact-message" class="ds-form__label">Ihre Nachricht *</label>
                                <textarea
                                    id="contact-message"
                                    v-model="form.message"
                                    rows="5"
                                    placeholder="Beschreiben Sie Ihr Anliegen..."
                                    class="ds-form__textarea"
                                    :class="{ 'ds-form__input--error': form.errors?.message }"
                                    required
                                ></textarea>
                                <span v-if="form.errors?.message" class="ds-form__error" role="alert">{{ form.errors.message }}</span>
                            </div>
                            <!-- Honeypot: hidden field bots fill in -->
                            <div aria-hidden="true" style="position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;">
                                <label>Website (bitte leer lassen)</label>
                                <input type="text" v-model="form.website" tabindex="-1" autocomplete="off" />
                            </div>
                            <button type="submit" :disabled="submitting" class="ds-form__submit cursor-pointer">
                                <svg v-if="submitting" class="ds-form__spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-dasharray="31.4 31.4" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" dur="0.8s" from="0 12 12" to="360 12 12" repeatCount="indefinite"/></circle></svg>
                                {{ submitting ? 'Wird gesendet...' : 'Nachricht absenden' }}
                                <PaperAirplaneIcon v-if="!submitting" class="ds-form__submit-icon" />
                            </button>
                            <p class="ds-form__privacy-note">
                                <ShieldCheckIcon class="ds-form__privacy-icon" aria-hidden="true" />
                                Ihre Daten werden gemäß der Datenschutzerklärung verarbeitet. Bei einer Angebotsanfrage geben wir sie an Fachbetriebe weiter.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
        </div>
        </VariantPageWrapper>
    </AppLayout>
</template>

<style scoped>
/* ===== HERO ===== */
.ds-hero {
    position: relative;
    min-height: var(--ds-hero-min-height, 280px);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dv-section, #f9f9f9);
    overflow: hidden;
    transition: min-height 0.3s ease;
}

.ds-hero--with-image {
    background-size: cover;
    background-position: center 40%;
    background-color: var(--dv-dark, #1a1a2e);
}

.ds-hero__overlay {
    position: absolute;
    inset: 0;
    background: var(--ds-hero-overlay-style, linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%));
    z-index: 1;
}

.ds-hero__glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse at 20% 50%, var(--dv-glow-a, rgba(137,111,218,0.25)) 0%, transparent 60%),
        radial-gradient(ellipse at 80% 30%, var(--dv-glow-b, rgba(251,144,125,0.2)) 0%, transparent 60%);
    opacity: var(--ds-hero-glow-intensity, 0.25);
    z-index: 2;
    mix-blend-mode: screen;
}

.ds-hero__inner {
    position: relative;
    z-index: 3;
    padding: 100px 0 60px;
    width: 100%;
}

.ds-hero:not(.ds-hero--with-image) .ds-hero__inner {
    padding: 64px 0 48px;
}

.ds-hero__badge {
    display: inline-block;
    padding: 6px 20px;
    border-radius: 24px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 24px;
}

.ds-hero__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-hero-title-size, clamp(28px, 4vw, 48px));
    font-weight: var(--ds-hero-title-weight, 700);
    line-height: 1.2;
    margin-bottom: 16px;
    text-shadow: var(--ds-hero-text-shadow, none);
    color: var(--dv-text, #333);
}

.ds-hero--with-image .ds-hero__title { color: #fff; }

.ds-hero__accent {
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-hero__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 17px;
    line-height: 1.7;
    max-width: 560px;
    margin: 0 auto;
    color: var(--dv-muted, #666);
}

.ds-hero--with-image .ds-hero__subtitle { color: rgba(255, 255, 255, 0.8); }

/* ===== SECTION ===== */
.ds-section {
    padding: 64px 0;
    background: var(--dv-page, #fff);
    position: relative;
}

@media (min-width: 768px) {
    .ds-section { padding: 80px 0; }
}

/* ===== CONTACT GRID ===== */
.ds-contact-grid {
    display: grid;
    gap: 32px;
    grid-template-columns: 1fr;
}

@media (min-width: 1024px) {
    .ds-contact-grid {
        grid-template-columns: 1fr 1fr;
        gap: 48px;
    }
}

/* ===== CONTACT DETAILS ===== */
.ds-contact-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* ===== DETAIL CARD ===== */
.ds-detail-card {
    display: flex;
    gap: 16px;
    padding: 24px;
    background: var(--dv-card, #fff);
    border: none;
    border-radius: var(--dv-radius-lg, 12px);
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    transform: translateZ(0);
    transition:
        transform 200ms ease-out,
        box-shadow 200ms ease-out;
}

.ds-detail-card:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06);
}

.ds-detail-card--accent {
    background: var(--brand-accent-soft, rgba(251,144,125,0.06));
    border-color: var(--dv-border, rgba(251,144,125,0.15));
    border-left: 3px solid var(--brand-accent);
}

.ds-detail-card__icon-wrap {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--brand-accent-soft, rgba(251,144,125,0.1));
    border-radius: 12px;
}

.ds-detail-card__icon {
    width: 22px;
    height: 22px;
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-detail-card__label {
    font-family: 'Lexend', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: var(--dv-text, #333);
    margin-bottom: 4px;
}

.ds-detail-card__link {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: var(--brand-accent-fg, var(--brand-accent));
    text-decoration: none;
    cursor: pointer;
    transition: opacity 200ms ease-out;
}

.ds-detail-card__link:hover {
    opacity: 0.8;
}

.ds-detail-card__link:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

.ds-detail-card__hint {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    color: var(--dv-muted, #999);
    margin-top: 4px;
    line-height: 1.5;
}

.ds-detail-card__text {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    color: var(--dv-muted, #666);
    line-height: 1.6;
}

/* ===== CONTACT IMAGE ===== */
.ds-contact-image {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: var(--dv-radius-xl, 16px);
    overflow: hidden;
    background-size: cover;
    background-position: center;
}

.ds-contact-image__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.1) 100%);
}

/* ===== FORM CARD ===== */
.ds-form-card {
    padding: 20px;
    background: var(--dv-card, #fafafa);
    border: none;
    border-radius: var(--dv-radius-lg, 12px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
    transform: translateZ(0);
}

@media (min-width: 480px) { .ds-form-card { padding: 28px; } }
@media (min-width: 640px) { .ds-form-card { padding: 36px; } }
@media (min-width: 1024px) { .ds-form-card { padding: 44px; } }

/* ===== FORM SUCCESS ===== */
.ds-form-card__success {
    text-align: center;
    padding: 40px 0;
}

.ds-form-card__success-icon-wrap {
    width: 64px;
    height: 64px;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(34, 197, 94, 0.1);
}

.ds-form-card__success-icon {
    width: 36px;
    height: 36px;
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-form-card__success-title {
    font-family: 'Lexend', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 10px;
}

.ds-form-card__success-text {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-muted, #666);
    line-height: 1.6;
}

.ds-form-card__success-link {
    color: var(--brand-accent-fg, var(--brand-accent));
    font-weight: 600;
}

.ds-form-card__success-link:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

/* ===== FORM ===== */
.ds-form {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.ds-form__title {
    font-family: 'Lexend', sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 4px;
    padding-bottom: 14px;
    border-bottom: 2px solid color-mix(in srgb, var(--brand-accent) 20%, transparent);
}

.ds-form__row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
}

@media (min-width: 640px) {
    .ds-form__row { grid-template-columns: 1fr 1fr; }
}

.ds-form__field {
    display: flex;
    flex-direction: column;
}

.ds-form__label {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--dv-text, #555);
    margin-bottom: 6px;
}

.ds-form__input {
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    background: var(--dv-card, #fff);
    border: 2px solid var(--dv-border, #e5e7eb);
    border-radius: var(--dv-radius-lg, 12px);
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-text, #333);
    outline: none;
    transition:
        border-color 200ms ease-out,
        box-shadow 200ms ease-out;
}

.ds-form__input:hover {
    border-color: var(--dv-muted, #ccc);
}

.ds-form__input:focus {
    border-color: var(--brand-accent);
    box-shadow: 0 0 0 3px rgba(251, 144, 125, 0.15);
}

.ds-form__input:focus-visible {
    outline: none;
}

.ds-form__input::placeholder {
    color: var(--dv-muted, #bbb);
}

.ds-form__textarea {
    width: 100%;
    min-height: 120px;
    padding: 12px 16px;
    background: var(--dv-card, #fff);
    border: 2px solid var(--dv-border, #e5e7eb);
    border-radius: var(--dv-radius-lg, 12px);
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-text, #333);
    outline: none;
    resize: vertical;
    transition:
        border-color 200ms ease-out,
        box-shadow 200ms ease-out;
}

.ds-form__textarea:hover {
    border-color: var(--dv-muted, #ccc);
}

.ds-form__textarea:focus {
    border-color: var(--brand-accent);
    box-shadow: 0 0 0 3px rgba(251, 144, 125, 0.15);
}

.ds-form__textarea:focus-visible {
    outline: none;
}

.ds-form__textarea::placeholder {
    color: var(--dv-muted, #bbb);
}

/* Submit button */
.ds-form__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 52px;
    padding: 14px 24px;
    background: var(--brand-accent);
    color: var(--brand-accent-text, #fff);
    border: none;
    border-radius: 26px;
    font-family: 'Lexend', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition:
        transform 200ms ease-out,
        box-shadow 200ms ease-out,
        opacity 200ms ease-out;
}

.ds-form__submit:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(251, 144, 125, 0.35);
}

.ds-form__submit:active:not(:disabled) {
    transform: translateY(0);
}

.ds-form__submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.ds-form__submit:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 3px;
}

.ds-form__submit-icon {
    width: 18px;
    height: 18px;
}

.ds-form__spinner {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

/* ===== SECTION FADE-IN ===== */
.section-fade-in {
    opacity: 0;
    transform: translateY(20px);
    animation: sectionReveal 0.6s ease-out forwards;
}

@keyframes sectionReveal {
    to { opacity: 1; transform: translateY(0); }
}

/* ===== TRUST STRIP ===== */
.ds-trust-strip {
    display: flex;
    align-items: center;
    gap: 8px 10px;
    padding: 12px 16px;
    background: var(--brand-accent-soft, rgba(251,144,125,0.06));
    border: 1px solid color-mix(in srgb, var(--brand-accent) 20%, transparent);
    border-radius: var(--dv-radius-lg, 12px);
    flex-wrap: wrap;
}

@media (min-width: 640px) {
    .ds-trust-strip { padding: 12px 20px; }
}

/* Hide separators on mobile when they wrap */
@media (max-width: 639px) {
    .ds-trust-strip__sep { display: none; }
    .ds-trust-strip { justify-content: center; }
}

.ds-trust-strip__item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--dv-text, #444);
}

.ds-trust-strip__icon {
    width: 15px;
    height: 15px;
    color: var(--brand-accent-fg, var(--brand-accent));
    flex-shrink: 0;
}

.ds-trust-strip__sep {
    color: var(--dv-border, #ddd);
    font-size: 12px;
    user-select: none;
}

/* Clock / info icon wrappers on detail cards */
.ds-detail-card__icon-wrap--clock,
.ds-detail-card__icon-wrap--info {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--brand-accent-soft, rgba(251,144,125,0.1));
    border-radius: 12px;
}

/* ===== FORM INTRO ===== */
.ds-form__intro {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    color: var(--dv-muted, #888);
    line-height: 1.5;
    margin-top: -8px;
}

/* ===== ERROR STATE ===== */
.ds-form__input--error {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}

.ds-form__error {
    display: block;
    margin-top: 5px;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    color: #ef4444;
    line-height: 1.4;
}

/* ===== PRIVACY NOTE ===== */
.ds-form__privacy-note {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 12px;
    color: var(--dv-muted, #aaa);
    line-height: 1.5;
    margin-top: -4px;
}

.ds-form__privacy-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--dv-muted, #bbb);
}

/* ===== SUCCESS ICON (SVG-based) ===== */
.ds-form-card__success-icon {
    width: 48px;
    height: 48px;
}

.smi-k {
    --dv-glow-a: rgba(15, 23, 42, 0.12);
    --dv-glow-b: rgba(234, 88, 12, 0.12);
    --brand-accent: #ea580c;
    background: #f8fafc;
    font-family: Inter, system-ui, sans-serif;
}
.smi-k .ds-hero__title { font-family: Fraunces, Georgia, serif; font-weight: 700; }
.smi-k .ds-hero:not(.ds-hero--with-image) { background: #0f172a; }
.smi-k .ds-hero:not(.ds-hero--with-image) .ds-hero__title,
.smi-k .ds-hero:not(.ds-hero--with-image) .ds-hero__subtitle { color: #f8fafc; }
</style>
